import React, { useEffect, useState } from "react";
import { Modal, Descriptions, Button, Select, Tag, Input, Spin, message, Popconfirm, DatePicker } from "antd";
import dayjs from "dayjs";
import { Pencil, Trash2 } from "lucide-react";
import {
  useEnquiryDetail,
  useUpdateEnquiryStatus,
  useAddEnquiryReply,
  useEditEnquiryCorrespondence,
  useDeleteEnquiryCorrespondence,
} from "../../../hooks/api/enquiry/hooks";
import { EnquiryStatusType } from "../../../types/api/enquiry";
import { ENQUIRY_TYPE_LABELS } from "./EnquiryColumns";

interface EnquiryDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  enquiryId: string | null;
}

const EnquiryDetailModal: React.FC<EnquiryDetailModalProps> = ({
  isOpen,
  onClose,
  enquiryId,
}) => {
  const [replyText, setReplyText] = useState("");
  // Oliver, Sept 2026: a wrong paste into Sales Correspondence (Max
  // Hawthorne's enquiry) had no way to be fixed except a developer
  // editing the database directly - edit/delete so admins can correct
  // mistakes themselves from here on.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");

  const { data, isLoading } = useEnquiryDetail(enquiryId || undefined, {
    enabled: isOpen && !!enquiryId,
  });
  const enquiry = data?.data;

  const updateStatusMutation = useUpdateEnquiryStatus({
    onSuccess: () => message.success("Status updated successfully"),
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || "Failed to update status"),
  });

  const addReplyMutation = useAddEnquiryReply({
    onSuccess: () => {
      message.success("Note added to correspondence");
      setReplyText("");
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || "Failed to save reply"),
  });

  const editCorrespondenceMutation = useEditEnquiryCorrespondence({
    onSuccess: () => {
      message.success("Correspondence entry updated");
      setEditingId(null);
      setEditingText("");
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || "Failed to update entry"),
  });

  const deleteCorrespondenceMutation = useDeleteEnquiryCorrespondence({
    onSuccess: () => message.success("Correspondence entry deleted"),
    onError: (err: any) =>
      message.error(err?.response?.data?.message || err?.message || "Failed to delete entry"),
  });

  // Was pre-filled with the single existing adminReply (overwrite
  // semantics) - now each save appends a new correspondence entry, so
  // the box should start empty and just clear again after a successful
  // save (see onSuccess below), not re-show the last note as if still
  // being edited.
  useEffect(() => {
    setReplyText("");
  }, [enquiry?._id]);

  if (!enquiryId) return null;

  const city = enquiry?.cityDoc?.name || (typeof enquiry?.cityId === "object" ? enquiry?.cityId?.name : "");
  const state = enquiry?.stateDoc?.name || (typeof enquiry?.stateId === "object" ? enquiry?.stateId?.name : "");
  const country = enquiry?.countryDoc?.name || (typeof enquiry?.countryId === "object" ? enquiry?.countryId?.name : "");

  const handleStatusChange = (status: EnquiryStatusType) => {
    if (!enquiryId) return;
    // Peter, Oct 2026: "Sold" status plus a place for the date - moving
    // to Sold with no date recorded yet defaults to today server-side
    // (see EnquiryService.updateStatus), so soldAt is only sent here to
    // preserve an existing value, never to force it blank.
    const soldAt = status === "SOLD" && enquiry?.soldAt ? enquiry.soldAt : undefined;
    updateStatusMutation.mutate({ id: enquiryId, status, soldAt });
  };

  const handleSoldDateChange = (date: dayjs.Dayjs | null) => {
    if (!enquiryId) return;
    updateStatusMutation.mutate({
      id: enquiryId,
      status: "SOLD",
      soldAt: date ? date.toISOString() : null,
    });
  };

  const handleSaveReply = () => {
    if (!enquiryId || !replyText.trim()) return;
    addReplyMutation.mutate({ id: enquiryId, reply: replyText.trim() });
  };

  const handleStartEdit = (id: string, text: string) => {
    setEditingId(id);
    setEditingText(text);
  };

  const handleSaveEdit = () => {
    if (!enquiryId || !editingId || !editingText.trim()) return;
    editCorrespondenceMutation.mutate({
      id: enquiryId,
      correspondenceId: editingId,
      text: editingText.trim(),
    });
  };

  const handleDeleteCorrespondence = (correspondenceId: string) => {
    if (!enquiryId) return;
    deleteCorrespondenceMutation.mutate({ id: enquiryId, correspondenceId });
  };

  return (
    <Modal
      title="Enquiry Lead Details"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={700}
      destroyOnClose
    >
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Spin size="large" />
        </div>
      ) : enquiry ? (
        <div className="space-y-6 pt-4">
          <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex gap-4">
              <div>
                <div className="text-xs text-slate-500">Type</div>
                <Tag color={enquiry.type === "CONTACT_US" ? "purple" : "geekblue"} className="mt-1">
                  {enquiry.type === "CONTACT_US" ? "Contact Us" : "Sculpture Enquiry"}
                </Tag>
              </div>
              <div>
                <div className="text-xs text-slate-500">Enquiry Type</div>
                <Tag
                  color={
                    (enquiry.enquiryType && ENQUIRY_TYPE_LABELS[enquiry.enquiryType]?.color) ||
                    "default"
                  }
                  className="mt-1"
                >
                  {(enquiry.enquiryType && ENQUIRY_TYPE_LABELS[enquiry.enquiryType]?.label) ||
                    "General"}
                </Tag>
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 text-right mb-1">Status</div>
              <div className="flex items-center gap-2">
                {/* Peter, Oct 2026: "Sold" status plus a place for the
                    date - the date picker only appears once Sold is
                    selected, defaulting to today if nothing's been set
                    yet (see EnquiryService.updateStatus). */}
                {(enquiry.status || "PENDING") === "SOLD" && (
                  <DatePicker
                    value={enquiry.soldAt ? dayjs(enquiry.soldAt) : dayjs()}
                    onChange={handleSoldDateChange}
                    disabled={updateStatusMutation.isPending}
                    className="w-32"
                    size="middle"
                  />
                )}
                <Select
                  value={enquiry.status || "PENDING"}
                  onChange={handleStatusChange}
                  loading={updateStatusMutation.isPending}
                  className="w-40"
                  options={[
                    { value: "PENDING", label: "Pending" },
                    { value: "IN_PROGRESS", label: "In Progress" },
                    { value: "RESOLVED", label: "Resolved" },
                    { value: "CLOSED", label: "Closed" },
                    { value: "SOLD", label: "Sold" },
                  ]}
                />
              </div>
            </div>
          </div>

          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="First Name">{enquiry.firstName || "-"}</Descriptions.Item>
            <Descriptions.Item label="Last Name">{enquiry.lastName || "-"}</Descriptions.Item>
            <Descriptions.Item label="Email">
              {enquiry.email ? (
                <a href={`mailto:${enquiry.email}`} className="text-blue-600 hover:underline">
                  {enquiry.email}
                </a>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            {/* Peter, Oct 2026: phone was several fields/a scroll away from
                the address - both now sit together, right up top. */}
            <Descriptions.Item label="Phone / Mobile">{enquiry.phone || "-"}</Descriptions.Item>
            <Descriptions.Item label="Address Line" span={2}>
              {enquiry.addressLine || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="City">{city || "-"}</Descriptions.Item>
            <Descriptions.Item label="State">{state || "-"}</Descriptions.Item>
            <Descriptions.Item label="Country">{country || "-"}</Descriptions.Item>
            <Descriptions.Item label="Postal / Zip Code">{enquiry.postalCode || "-"}</Descriptions.Item>
            {/* Peter, Oct 2026: "2 or 3 which don't seem to be attached to
                any particular piece" - this row now always shows, with an
                explicit "No specific sculpture" instead of silently
                vanishing, so a genuinely general enquiry reads as
                confirmed-general rather than looking like a missed link. */}
            <Descriptions.Item label="Sculpture" span={2}>
              {enquiry.productDoc?.title || enquiry.artworkName ? (
                <>
                  {enquiry.productDoc?.title || enquiry.artworkName}
                  {enquiry.artistDoc?.name && ` (by ${enquiry.artistDoc.name})`}
                </>
              ) : (
                <span className="text-slate-400 italic">No specific sculpture</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Submitted At" span={2}>
              {enquiry.createdAt ? new Date(enquiry.createdAt).toLocaleString() : "-"}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <h4 className="font-semibold text-slate-800 mb-2">Enquiry Message:</h4>
            <div className="bg-slate-50 p-4 rounded border border-slate-200 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {enquiry.message || "No message provided."}
            </div>
          </div>

          <div>
            {/* Peter, Sept 2026: sales correspondence kept against the
                enquiry for future reference, not just a single
                overwritable reply - a running log of notes/emails/calls
                exchanged with the buyer as a sale progresses. */}
            <h4 className="font-semibold text-slate-800 mb-2">Sales Correspondence</h4>
            {enquiry.correspondence && enquiry.correspondence.length > 0 ? (
              <div className="flex flex-col gap-2 mb-3 max-h-64 overflow-y-auto">
                {enquiry.correspondence.map((entry: any, i: number) => (
                  <div
                    key={entry._id || i}
                    className="bg-slate-50 p-3 rounded border border-slate-200 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap group"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="text-xs text-slate-400">
                        {new Date(entry.createdAt).toLocaleString()} · {entry.author}
                        {entry.editedAt && " · (edited)"}
                      </div>
                      {entry._id && editingId !== entry._id && (
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <Button
                            type="text"
                            size="small"
                            icon={<Pencil className="w-3.5 h-3.5 text-slate-500" />}
                            onClick={() => handleStartEdit(entry._id, entry.text)}
                            title="Edit entry"
                          />
                          <Popconfirm
                            title="Delete entry"
                            description="Are you sure you want to delete this correspondence entry?"
                            onConfirm={() => handleDeleteCorrespondence(entry._id)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<Trash2 className="w-3.5 h-3.5 text-red-500" />}
                              title="Delete entry"
                            />
                          </Popconfirm>
                        </div>
                      )}
                    </div>
                    {editingId === entry._id ? (
                      <div className="space-y-2">
                        <Input.TextArea
                          rows={3}
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          autoFocus
                        />
                        <div className="flex justify-end gap-2">
                          <Button
                            size="small"
                            onClick={() => {
                              setEditingId(null);
                              setEditingText("");
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            onClick={handleSaveEdit}
                            loading={editCorrespondenceMutation.isPending}
                            disabled={!editingText.trim()}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      entry.text
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic mb-3">
                Nothing recorded yet.
              </p>
            )}
            <Input.TextArea
              rows={3}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Add a note - e.g. a summary of an email or call with the buyer..."
            />
            <div className="flex justify-end mt-2">
              <Button
                type="primary"
                onClick={handleSaveReply}
                loading={addReplyMutation.isPending}
                disabled={!replyText.trim()}
              >
                Add to Correspondence
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-slate-500">Enquiry not found.</div>
      )}
    </Modal>
  );
};

export default EnquiryDetailModal;
